import React, { useEffect, useRef } from "react";

import ContainerLandpage from "containers/Landpage";
import {
  PageRoot,
  TopBanner,
  TopBannerBackdrop,
  TopBannerInner,
  TopBannerEyebrow,
  TopBannerTitle,
  TopBannerText,
  SectionContainer,
  SectionBlock,
  SectionEyebrow,
  SectionTitle,
  SectionText,
  StepsGrid,
  StepCard,
  StepNumber,
  StepTitle,
  StepText,
  PostOperativeFeatureGrid,
  PostOperativeFeatureCard,
  PostOperativeFeatureMeta,
  PostOperativeFeatureLabel,
  PostOperativeFeatureTitle,
  PostOperativeFeatureText,
  PostOperativeTreatments,
  PostOperativeTreatmentItem,
  RankingSection,
  RankingGrid,
  RankingCard,
  RankingPosition,
  RankingInfo,
  RankingTitle,
  RankingMeta,
  TreatmentsSection,
  TreatmentsGrid,
  TreatmentCard,
  TreatmentName,
  TreatmentDescription,
  TreatmentButton,
  AboutSection,
  AboutPhoto,
  AboutBody,
  AboutName,
  AboutParagraph,
  HighlightsGrid,
  HighlightCard,
  HighlightValue,
  HighlightLabel,
  FeedbackSection,
  FeedbackHeader,
  FeedbackGrid,
  FeedbackActionWrap,
  FeedbackActionButton,
  FeedbackCard,
  FeedbackStars,
  FeedbackQuote,
  FeedbackAuthor,
  FeedbackMeta,
  OfferSection,
  OfferCard,
  OfferLead,
  OfferTitle,
  OfferText,
  OfferButton,
  LocationSection,
  LocationCard,
  LocationInfo,
  LocationLead,
  LocationTitle,
  LocationAddress,
  LocationAction,
  LocationMap,
  LocationMapFrame,
  LocationFootnote,
} from "screens/LandpageAesthetics/styled";

const careSteps = [
  {
    title: "Avaliação clínica no domicílio",
    image: "/images/avaliacao.jpg",
    text: "Consulta inicial para analisar o quadro atual, prescrições, rotina da casa e fatores de risco com foco em segurança.",
  },
  {
    title: "Plano de cuidado personalizado",
    image: "/images/plano.jpg",
    text: "Organização de condutas, frequência de visitas e metas terapêuticas para estomaterapia, curativos e reabilitação.",
  },
  {
    title: "Administração segura de medicamentos",
    image: "/images/med.jpg",
    text: "Aplicação e manejo de medicamentos conforme prescrição médica, com controle de horários, dose e monitoramento de respostas.",
  },
  {
    title: "Monitoramento e orientação da família",
    image: "/images/monitoramento.jpg",
    text: "Acompanhamento contínuo da evolução com ajustes de conduta e orientações práticas para manter o cuidado entre as visitas.",
  },
];

const facialPostOperativeCare = [
  {
    label: "Enfermagem especializada",
    title: "Segurança e acompanhamento em cada fase",
    text: "Monitoramento de sinais clínicos, cuidados com curativos, controle de edema, orientação sobre medicamentos e identificação precoce de intercorrências para uma recuperação mais segura.",
  },
  {
    label: "Fisioterapia dermatofuncional e neurossensorial",
    title: "Recuperação funcional com conforto e precisão",
    text: "Técnicas individualizadas auxiliam na redução do edema, melhora da mobilidade dos tecidos, prevenção de fibroses e recuperação da sensibilidade, respeitando o tempo biológico do pós-operatório.",
  },
];

const facialPostOperativeTreatments = [
  "Drenagem linfática facial",
  "Alta frequência",
  "Ultrassom terapêutico",
  "Laserterapia",
  "Liberação miofascial",
  "Curativos especializados",
  "Técnicas de manipulação tecidual específicas para cada tipo de cirurgia",
  "Bandagem com Kinesio Taping",
];

const rankingTreatments = [
  {
    title: "Cuidados pós-operatórios",
    share: 37,
    meta: "Acompanhamento clínico para recuperação segura e orientada.",
  },
  {
    title: "Administração de medicamentos",
    share: 32,
    meta: "Protocolos seguros de medicação conforme prescrição médica.",
  },
  {
    title: "Laserterapia",
    share: 29,
    meta: "Recurso complementar para controle de dor e reparação tecidual.",
  },
  {
    title: "Home Care",
    share: 24,
    meta: "Atendimento contínuo para pacientes com demandas de média e alta complexidade.",
  },
  {
    title: "Tratamento de feridas",
    share: 21,
    meta: "Curativos especializados com foco em cicatrização eficiente.",
  },
  {
    title: "Estomaterapia",
    share: 18,
    meta: "Manejo técnico com acolhimento para paciente e rede de apoio.",
  },
];

const nursingTreatments = [
  {
    title: "Tratamento de feridas",
    description: "Curativos avançados e acompanhamento da evolução da cicatrização.",
    message: "Olá, quero detalhes sobre tratamento de feridas em domicílio.",
  },
  {
    title: "Estomaterapia",
    description: "Cuidado especializado para estomias e lesões relacionadas.",
    message: "Olá, quero saber mais sobre estomaterapia em domicílio.",
  },
  {
    title: "Laserterapia",
    description: "Tecnologia aplicada para suporte na recuperação e conforto clínico.",
    message: "Olá, quero agendar uma avaliação para laserterapia.",
  },
  {
    title: "Cuidados pós-operatórios",
    description: "Assistência no pós-cirúrgico com monitoramento e condutas seguras.",
    message: "Olá, quero orientações sobre cuidados pós-operatórios.",
  },
  {
    title: "Administração de medicamentos",
    description: "Aplicação e controle de terapias com rigor técnico e segurança.",
    message: "Olá, preciso de administração de medicamentos em casa.",
  },
  {
    title: "Enfermagem ortomolecular",
    description: "Abordagem integrativa para suporte metabólico e bem-estar global.",
    message: "Olá, quero conhecer os protocolos de enfermagem ortomolecular.",
  },
  {
    title: "Reabilitação geriátrica",
    description: "Plano assistencial para idosos com foco em autonomia, mobilidade e qualidade de vida.",
    message: "Olá, quero saber mais sobre reabilitação geriátrica domiciliar.",
  },
  {
    title: "Kinesio Taping",
    description: "Aplicação de bandagens funcionais para suporte muscular, analgesia e recuperação.",
    message: "Olá, quero saber mais sobre o tratamento com Kinesio Taping.",
  },
  {
    title: "Plano terapêutico para paciente e família",
    description: "Estratégias de cuidado compartilhadas para segurança e continuidade do tratamento.",
    message: "Olá, quero montar um plano terapêutico para paciente e família.",
  },
  {
    title: "Atendimento a pedido médico",
    description: "Execução de condutas prescritas com técnica, pontualidade e monitoramento.",
    message: "Olá, preciso de atendimento de enfermagem a pedido médico.",
  },
  {
    title: "Reabilitação fisioterapêutica em pós-operatório",
    description: "Acompanhamento focado na recuperação funcional e motora no período pós-cirúrgico.",
    message: "Olá, quero uma avaliação para reabilitação fisioterapêutica em pós-operatório.",
  },
  {
    title: "Cuidados de enfermagem personalizados",
    description: "Atendimento individualizado conforme rotina, diagnóstico e objetivos do paciente.",
    message: "Olá, quero saber mais sobre cuidados de enfermagem personalizados.",
  },
];

const feedbacks = [
  {
    author: "M. S.",
    meta: "Paciente Home Care",
    quote:
      "Excelente acompanhamento no pós-operatório. A evolução foi rápida, com orientações claras para toda a família.",
  },
  {
    author: "R. A.",
    meta: "Familiar de paciente",
    quote:
      "A administração de medicamentos foi feita com extrema segurança e pontualidade. Sentimos confiança desde o primeiro atendimento.",
  },
  {
    author: "P. C.",
    meta: "Paciente em reabilitação",
    quote:
      "Atendimento humanizado de verdade. O plano terapêutico foi ajustado para minha rotina e os resultados apareceram semana a semana.",
  },
  {
    author: "L. T.",
    meta: "Paciente",
    quote:
      "Profissionalismo, técnica e acolhimento. O suporte foi fundamental para meu processo de recuperação em casa.",
  },
  {
    author: "E. N.",
    meta: "Familiar de paciente acamado",
    quote:
      "A equipe trouxe tranquilidade para toda a casa. O cuidado foi atencioso, claro e extremamente organizado.",
  },
  {
    author: "A. B.",
    meta: "Paciente pós-cirúrgico",
    quote:
      "Recebi acompanhamento impecável no meu pós-operatório. Cada visita me deixou mais segura e confiante.",
  },
];

const address = "Atendimento domiciliar em Brasília e Entorno - DF";
const mapQuery = "Distrito Federal e Entorno";
const mapsEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;
const mapsOpenUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;

const openWhatsApp = (text) =>
  window.open(`https://wa.me/61998333005?text=${encodeURIComponent(text)}`, "_blank");

export default function Landpage() {
  const rootRef = useRef(null);

  useEffect(() => {
    const gsap = window?.gsap;
    const ScrollTrigger = window?.ScrollTrigger;

    if (!gsap) {
      return undefined;
    }

    if (ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
    }

    const ctx = gsap.context(() => {
      const shouldReduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      const parallaxBackground = rootRef.current?.querySelector("[data-parallax-bg]");

      if (parallaxBackground && ScrollTrigger && !shouldReduceMotion && window.innerWidth > 820) {
        gsap.fromTo(
          parallaxBackground,
          { yPercent: -7, scale: 1.08 },
          {
            yPercent: 7,
            scale: 1.13,
            ease: "none",
            scrollTrigger: {
              trigger: "#banner",
              start: "top top",
              end: "bottom top",
              scrub: true,
            },
          }
        );
      }

      gsap.utils.toArray("[data-reveal]").forEach((item) => {
        gsap.fromTo(
          item,
          { autoAlpha: 0, y: 48 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: ScrollTrigger
              ? {
                  trigger: item,
                  start: "top 84%",
                }
              : undefined,
          }
        );
      });

      const feedbackCards = gsap.utils.toArray("[data-feedback-card]");
      if (feedbackCards.length) {
        gsap.fromTo(
          feedbackCards,
          { autoAlpha: 0, y: 70, scale: 0.96, filter: "blur(6px)" },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            duration: 0.95,
            ease: "power3.out",
            stagger: gsap.utils.distribute({
              base: 0,
              amount: 0.85,
              from: "center",
              grid: "auto",
            }),
            scrollTrigger: ScrollTrigger
              ? {
                  trigger: "[data-feedback-grid]",
                  start: "top 82%",
                }
              : undefined,
          }
        );
      }
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <ContainerLandpage variant="nursing">
      <PageRoot ref={rootRef}>
        <TopBanner id="banner" data-reveal $darkOverlay>
          <TopBannerBackdrop data-parallax-bg $image="/images/fundo%20verde.jpg" />
          <TopBannerInner>
            <TopBannerEyebrow>Brasília e Entorno</TopBannerEyebrow>
            <TopBannerTitle>Enfermagem especializada com cuidado humanizado</TopBannerTitle>
            <TopBannerText>
              Atendimento domiciliar com protocolos personalizados para recuperação segura,
              monitoramento clínico e suporte contínuo para paciente e família.
            </TopBannerText>
          </TopBannerInner>
        </TopBanner>

        <SectionBlock id="facial-post-operative" data-reveal light>
          <SectionContainer vertical>
            <SectionEyebrow>Pós-operatório de cirurgias faciais</SectionEyebrow>
            <SectionTitle>Cuidado integrado para uma recuperação mais segura</SectionTitle>
            <SectionText>
              O acompanhamento especializado após cirurgias faciais contribui para controlar
              sintomas, prevenir complicações e favorecer uma evolução mais confortável e
              previsível, sempre em alinhamento com a equipe médica.
            </SectionText>

            <PostOperativeFeatureGrid>
              {facialPostOperativeCare.map((item) => (
                <PostOperativeFeatureCard key={item.label} data-reveal>
                  <PostOperativeFeatureMeta>
                    <PostOperativeFeatureLabel>{item.label}</PostOperativeFeatureLabel>
                  </PostOperativeFeatureMeta>
                  <PostOperativeFeatureTitle>{item.title}</PostOperativeFeatureTitle>
                  <PostOperativeFeatureText>{item.text}</PostOperativeFeatureText>
                </PostOperativeFeatureCard>
              ))}
            </PostOperativeFeatureGrid>

            <PostOperativeTreatments aria-label="Tratamentos oferecidos no pós-operatório facial">
              {facialPostOperativeTreatments.map((treatment) => (
                <PostOperativeTreatmentItem key={treatment} data-reveal>
                  {treatment}
                </PostOperativeTreatmentItem>
              ))}
            </PostOperativeTreatments>
          </SectionContainer>
        </SectionBlock>

        <SectionBlock id="homecare" data-reveal>
          <SectionContainer vertical>
            <SectionEyebrow>Método de cuidado</SectionEyebrow>
            <SectionTitle>Como funciona o atendimento domiciliar</SectionTitle>
            <SectionText>
              Um processo estruturado para garantir atendimento humanizado, administração de
              medicamentos com segurança e evolução clínica em cada fase do tratamento.
            </SectionText>

            <StepsGrid>
              {careSteps.map((step, index) => (
                <StepCard key={step.title} data-reveal $uniform $image={step.image}>
                  <StepNumber>{String(index + 1).padStart(2, "0")}</StepNumber>
                  <StepTitle>{step.title}</StepTitle>
                  <StepText>{step.text}</StepText>
                </StepCard>
              ))}
            </StepsGrid>
          </SectionContainer>
        </SectionBlock>

        <RankingSection data-reveal>
          <SectionContainer vertical>
            <SectionEyebrow>Ranking</SectionEyebrow>
            <SectionTitle>Serviços mais procurados em enfermagem</SectionTitle>
            <SectionText>
              Procedimentos com maior demanda no atendimento domiciliar, considerando perfil
              clínico e necessidade de acompanhamento contínuo.
            </SectionText>

            <RankingGrid>
              {rankingTreatments.map((item, index) => (
                <RankingCard key={item.title} data-reveal>
                  <RankingPosition>{String(index + 1).padStart(2, "0")}</RankingPosition>
                  <RankingInfo>
                    <RankingTitle>{item.title}</RankingTitle>
                    <RankingMeta>
                      {item.share}% de procura • {item.meta}
                    </RankingMeta>
                  </RankingInfo>
                </RankingCard>
              ))}
            </RankingGrid>
          </SectionContainer>
        </RankingSection>

        <TreatmentsSection id="services" data-reveal>
          <SectionContainer vertical>
            <SectionEyebrow>Catálogo completo</SectionEyebrow>
            <SectionTitle>Conheça os serviços de enfermagem</SectionTitle>
            <SectionText>
              Assistência clínica personalizada para diferentes perfis de cuidado, com conduta
              técnica, acompanhamento da evolução e orientação clara para a família.
            </SectionText>

            <TreatmentsGrid>
              {nursingTreatments.map((item) => (
                <TreatmentCard key={item.title} data-reveal>
                  <TreatmentName>{item.title}</TreatmentName>
                  <TreatmentDescription>{item.description}</TreatmentDescription>
                  <TreatmentButton type="button" onClick={() => openWhatsApp(item.message)}>
                    Solicitar atendimento
                  </TreatmentButton>
                </TreatmentCard>
              ))}
            </TreatmentsGrid>
          </SectionContainer>
        </TreatmentsSection>

        <AboutSection id="about" data-reveal>
          <SectionContainer>
            <AboutPhoto src="/images/dra-nova.jpg" alt="Profissional de enfermagem especializada" />
            <AboutBody>
              <SectionEyebrow>Sobre a profissional</SectionEyebrow>
              <AboutName>Dra. Heloisa Andrade</AboutName>
              <AboutParagraph>
                Graduada em Enfermagem com Especialização em Tratamentos de Feridas,
                Estomaterapia, Cuidados Pós-Operatórios, Centro Cirúrgico e CME, Graduada em
                Gestão em Saúde Pública, com vasta experiência em Home Care, Cuidados de PO de
                cirurgia plástica, Estética Corporal, Facial e Capilar, Centro Cirúrgico e CME.
                Com atuação voltada à excelência, segurança e personalização no cuidado Home Care.
                <br />
                <br />
                Com atendimento humanizado e olhar técnico apurado, realiza procedimentos em
                consultório com protocolos modernos e individualizados.
                <br />
                <br />
                Além dos atendimentos em ambiente clínico, também oferece serviços em home care,
                proporcionando praticidade, conforto e exclusividade para pacientes que desejam
                receber cuidados especializados no conforto de casa, sem abrir mão da qualidade e
                do padrão profissional.
              </AboutParagraph>

              <HighlightsGrid>
                <HighlightCard>
                  <HighlightValue>DF</HighlightValue>
                  <HighlightLabel>Atendimento em Brasília e entorno</HighlightLabel>
                </HighlightCard>
                <HighlightCard>
                  <HighlightValue>1:1</HighlightValue>
                  <HighlightLabel>Plano de cuidado exclusivo por paciente</HighlightLabel>
                </HighlightCard>
                <HighlightCard>
                  <HighlightValue>24h</HighlightValue>
                  <HighlightLabel>Suporte de orientação para família e cuidadores</HighlightLabel>
                </HighlightCard>
              </HighlightsGrid>
            </AboutBody>
          </SectionContainer>
        </AboutSection>

        <FeedbackSection id="feedback" data-reveal>
          <SectionContainer vertical>
            <FeedbackHeader>
              <SectionEyebrow>Feedback</SectionEyebrow>
              <SectionTitle>O que pacientes e familiares relatam</SectionTitle>
              <SectionText>
                Depoimentos reais sobre segurança no cuidado, clareza nas orientações e
                acolhimento durante todo o processo de recuperação.
              </SectionText>
            </FeedbackHeader>

            <FeedbackGrid data-feedback-grid>
              {feedbacks.map((feedback) => (
                <FeedbackCard key={`${feedback.author}-${feedback.meta}`} data-feedback-card>
                  <FeedbackStars>★★★★★</FeedbackStars>
                  <FeedbackQuote>{feedback.quote}</FeedbackQuote>
                  <FeedbackAuthor>{feedback.author}</FeedbackAuthor>
                  <FeedbackMeta>{feedback.meta}</FeedbackMeta>
                </FeedbackCard>
              ))}
            </FeedbackGrid>

            <FeedbackActionWrap>
              <FeedbackActionButton
                type="button"
                onClick={() =>
                  openWhatsApp("Olá, quero deixar minha avaliação sobre o atendimento de enfermagem.")
                }
              >
                Deixe sua avaliação
              </FeedbackActionButton>
            </FeedbackActionWrap>
          </SectionContainer>
        </FeedbackSection>

        <OfferSection data-reveal>
          <SectionContainer vertical>
            <OfferCard>
              <div>
                <OfferLead>Consulta estratégica</OfferLead>
                <OfferTitle>Receba seu plano de cuidados de enfermagem</OfferTitle>
                <OfferText>
                  Envie uma mensagem e receba orientações iniciais, formato do acompanhamento
                  e sugestão de rotina de visitas para o seu caso.
                </OfferText>
              </div>
              <OfferButton
                onClick={() =>
                  openWhatsApp("Olá, quero receber meu plano de cuidados de enfermagem.")
                }
              >
                Solicitar plano
              </OfferButton>
            </OfferCard>
          </SectionContainer>
        </OfferSection>

        <LocationSection id="location" data-reveal>
          <SectionContainer vertical>
            <LocationCard>
              <LocationInfo>
                <LocationLead>Localização</LocationLead>
                <LocationTitle>Base de atendimento</LocationTitle>
                <LocationAddress>{address}</LocationAddress>
                <LocationAction onClick={() => window.open(mapsOpenUrl, "_blank")}>
                  Abrir no Google Maps
                </LocationAction>
              </LocationInfo>

              <LocationMap>
                <LocationMapFrame
                  title="Mapa de cobertura de atendimento em DF e Entorno"
                  src={mapsEmbedUrl}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </LocationMap>
            </LocationCard>
            <LocationFootnote>
              Atendimento de enfermagem mediante agendamento prévio.
            </LocationFootnote>
          </SectionContainer>
        </LocationSection>
      </PageRoot>
    </ContainerLandpage>
  );
}
